import { Activity, Droplets, Flame, Plus, Sparkles } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal } from "./Reveal";

const features = [
  {
    icon: Sparkles,
    title: "Red light · 630, 660 & 670nm",
    body: "Surface wavelengths absorbed by the skin — used to support skin tone, collagen and circulation. The glow you leave with is real.",
  },
  {
    icon: Activity,
    title: "Near-infrared · 810, 830, 850 & 940nm",
    body: "Invisible NIR travels deeper into muscles and joints — the perfect chaser to a heat-and-cold circuit when your body is primed for recovery.",
  },
  {
    icon: Droplets,
    title: "9 wavelengths, quad-tip LED chips",
    body: "A patented 9-wavelength design (including 480nm blue and 590nm yellow) through 192 medical-grade quad-chip LEDs — flicker-free, UV-free and EMF-free, delivering over 140mW/cm² of light.",
  },
  {
    icon: Flame,
    title: "Built to live in the sauna",
    body: "IP65-sealed and heat-rated to 90°C, the panel runs right there in the heat. Bathe in deep red light while the sauna works — two therapies in one sitting.",
  },
];

const stats = [
  ["9", "wavelengths"],
  ["192", "quad-chip LEDs"],
  ["140+", "mW/cm² irradiance"],
  ["90°C", "heat-rated"],
];

const RedLight = () => {
  return (
    <section data-testid="red-light-section" className="bg-coal border-y border-line/60">
      <div className="mx-auto max-w-7xl grid lg:grid-cols-2">
        {/* ── copy: left ── */}
        <div className="order-2 lg:order-1 px-5 sm:px-8 py-16 md:py-24 lg:pr-16 lg:pl-8 flex flex-col justify-center">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase font-medium text-rust">
              Coming soon
            </p>
            <h2 className="mt-4 font-display uppercase text-3xl sm:text-4xl tracking-wide text-cream">
              Finish in the <em className="italic text-ember">red</em>.
            </h2>
            <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-sand max-w-xl">
              Inside the sauna hangs the TLA Sauna Tech — a full-body red-light
              therapy panel of the kind you'd normally find in recovery clinics.
              Switch it on, sit back, and let nine wavelengths of red and
              near-infrared light wash over you while the heat does its work.
            </p>
            <div className="mt-6 border-l-2 border-rust pl-5 max-w-xl">
              <p className="text-base font-light leading-relaxed text-sand">
                <span className="text-cream font-medium">The best of both worlds.</span>{" "}
                Traditional dry sauna heat opens your blood vessels and gets
                circulation surging — exactly the state where light therapy is
                thought to work best. One sitting, two modalities: the deep,
                time-tested heat of a Finnish sauna and clinic-grade red light,
                working on your body at the same time.
              </p>
            </div>

            {/* mobile: the image lives inside the card, not above it */}
            <div className="relative lg:hidden mt-10 flex justify-center">
              <div
                className="absolute left-1/2 top-1/2 h-[80%] w-[90%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[70px] animate-glow-pulse"
                style={{ background: "radial-gradient(ellipse at center, rgba(255,46,77,0.3) 0%, rgba(255,46,77,0.1) 55%, transparent 78%)" }}
                aria-hidden="true"
              />
              <img
                src={IMAGES.redLight}
                alt="Woman relaxing in the sauna under the TLA Sauna Tech full-body red light therapy panel"
                loading="lazy"
                data-testid="redlight-image-mobile"
                className="relative w-4/5 max-w-[320px] h-auto select-none drop-shadow-[0_24px_48px_rgba(0,0,0,0.85)]"
              />
            </div>
          </Reveal>

          <div className="mt-10 space-y-6">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={0.08 + i * 0.07}>
                <div className="flex gap-4" data-testid={`redlight-feature-${i + 1}`}>
                  <div className="shrink-0 flex h-10 w-10 items-center justify-center border border-rust/40 bg-rust/10">
                    <f.icon className="h-4 w-4 text-ember" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-cream">{f.title}</h3>
                    <p className="mt-1 text-sm font-light leading-relaxed text-sand">{f.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div data-testid="redlight-stats" className="mt-10 grid grid-cols-4 divide-x divide-line border border-line bg-ink max-w-xl">
              {stats.map(([v, label]) => (
                <div key={label} className="px-3 py-4 text-center">
                  <p className="font-display text-xl sm:text-2xl text-ember">{v}</p>
                  <p className="mt-1 text-[9px] sm:text-[10px] tracking-[0.12em] uppercase text-stone2 leading-tight">{label}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm font-light text-sand max-w-xl">
              <span className="text-cream font-medium">Coming soon</span> — the
              panel is being commissioned and isn't available to purchase yet.
              We'll switch it on here the moment it's ready; members will enjoy it
              on the house once commissioned.
            </p>
            <div className="mt-6 flex items-start gap-3 border border-line bg-ink p-5 max-w-xl">
              <Plus className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
              <p className="text-sm font-light leading-relaxed text-stone2">
                Red-light therapy is offered as a wellness add-on only. It is not a
                medical treatment, and we don't promise health outcomes.
              </p>
            </div>
          </Reveal>
        </div>

        {/* ── image: right (desktop only — embedded in the card on mobile) ── */}
        <div className="relative hidden lg:flex items-center justify-center lg:min-h-[560px] order-1 lg:order-2 px-6 py-12 lg:py-20 overflow-hidden">
          {/* red mood glow */}
          <div
            className="absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[100px] animate-glow-pulse"
            style={{ background: "radial-gradient(ellipse at center, rgba(255,46,77,0.28) 0%, rgba(255,46,77,0.1) 50%, transparent 75%)" }}
            aria-hidden="true"
          />
          <Reveal className="relative">
            <img
              src={IMAGES.redLight}
              alt="Woman relaxing in the sauna under the TLA Sauna Tech full-body red light therapy panel"
              loading="lazy"
              className="relative h-auto w-full max-w-[420px] lg:max-w-[480px] select-none drop-shadow-[0_30px_60px_rgba(0,0,0,0.85)]"
            />
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 h-6 w-3/4 rounded-[100%] bg-black/80 blur-xl" aria-hidden="true" />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default RedLight;
